/**
 * Phase G onboarding constants — chip grids + labels mirroring
 * /app/backend/onboarding_labels.py. Single source of truth on the client.
 */

export const OPTIONS = {
  building: [
    { value: "saas",         label: "SaaS platform" },
    { value: "mobile_app",   label: "Mobile app" },
    { value: "ecommerce",    label: "E-commerce store" },
    { value: "marketplace",  label: "Marketplace" },
    { value: "ai_tool",      label: "AI tool" },
    { value: "agency",       label: "Agency / service" },
    { value: "community",    label: "Community / media" },
    { value: "fintech",      label: "Fintech" },
    { value: "healthtech",   label: "Healthtech" },
    { value: "other",        label: "Other" },
  ],
  target_customer: [
    { value: "consumers",         label: "Everyday consumers" },
    { value: "small_businesses",  label: "Small businesses" },
    { value: "enterprises",       label: "Enterprises" },
    { value: "creators",          label: "Creators & influencers" },
    { value: "students",          label: "Students" },
    { value: "parents",           label: "Parents & families" },
    { value: "developers",        label: "Developers" },
    { value: "local_businesses",  label: "Local businesses" },
    { value: "other",             label: "Other" },
  ],
  core_problem: [
    { value: "saves_time",       label: "Saves time" },
    { value: "makes_money",      label: "Makes money" },
    { value: "reduces_costs",    label: "Reduces costs" },
    { value: "improves_health",  label: "Improves health" },
    { value: "entertainment",    label: "Entertainment" },
    { value: "education",        label: "Education" },
    { value: "convenience",      label: "Convenience" },
    { value: "automation",       label: "Automation" },
    { value: "other",            label: "Other" },
  ],
  revenue_model: [
    { value: "subscription",       label: "Subscription" },
    { value: "one_time",           label: "One-time sales" },
    { value: "marketplace_fees",   label: "Marketplace fees" },
    { value: "ads",                label: "Advertising" },
    { value: "services",           label: "Services" },
    { value: "licensing",          label: "Licensing" },
    { value: "freemium",           label: "Freemium" },
    { value: "unsure",             label: "Not sure yet" },
  ],
  startup_stage: [
    { value: "idea_only",      label: "Idea only" },
    { value: "researching",    label: "Researching" },
    { value: "building_mvp",   label: "Building MVP" },
    { value: "beta_users",     label: "Live with beta users" },
    { value: "launched",       label: "Publicly launched" },
    { value: "revenue",        label: "Making revenue" },
    { value: "scaling",        label: "Scaling rapidly" },
  ],
  main_goal: [
    { value: "validate_idea",   label: "Validate the idea" },
    { value: "build_mvp",       label: "Build MVP" },
    { value: "launch_public",   label: "Launch publicly" },
    { value: "first_10",        label: "First 10 customers" },
    { value: "mrr_1k",          label: "Reach $1K MRR" },
    { value: "mrr_10k",         label: "Reach $10K MRR" },
    { value: "raise_funding",   label: "Raise funding" },
    { value: "scale_ops",       label: "Scale operations" },
    { value: "exit",            label: "Prepare for exit" },
  ],
  target_timeline: [
    { value: "30d",   label: "30 days" },
    { value: "60d",   label: "60 days" },
    { value: "90d",   label: "90 days" },
    { value: "6mo",   label: "6 months" },
    { value: "12mo",  label: "12 months" },
  ],
  success_90d: [
    { value: "launch_mvp",    label: "Launch MVP" },
    { value: "first_paying",  label: "First paying customers" },
    { value: "mrr_1k",        label: "$1K MRR" },
    { value: "mrr_10k",       label: "$10K MRR" },
    { value: "raise_capital", label: "Raise capital" },
    { value: "build_team",    label: "Build the team" },
    { value: "viral_growth",  label: "Viral growth" },
    { value: "clear_roadmap", label: "Clear roadmap" },
  ],
  target_market: [
    { value: "local_city",     label: "Local city" },
    { value: "national",       label: "National" },
    { value: "africa",         label: "Africa" },
    { value: "europe",         label: "Europe" },
    { value: "north_america",  label: "North America" },
    { value: "global_remote",  label: "Global / remote-first" },
    { value: "other",          label: "Other" },
  ],
  budget_available: [
    { value: "b_0_500",    label: "$0 – $500" },
    { value: "b_500_2k",   label: "$500 – $2K" },
    { value: "b_2k_10k",   label: "$2K – $10K" },
    { value: "b_10k_50k",  label: "$10K – $50K" },
    { value: "b_50k_plus", label: "$50K+" },
  ],
  funding_source: [
    { value: "self",         label: "Self-funded" },
    { value: "side_income",  label: "Side income" },
    { value: "ff",           label: "Friends & family" },
    { value: "investors",    label: "Investors" },
    { value: "revenue",      label: "Customer revenue" },
    { value: "looking",      label: "Looking for funding" },
  ],
  team_size: [
    { value: "solo",     label: "Solo founder" },
    { value: "2",        label: "2 people" },
    { value: "3_5",      label: "3 – 5 people" },
    { value: "6_10",     label: "6 – 10 people" },
    { value: "10_plus",  label: "10+ people" },
  ],
  founder_role: [
    { value: "technical",       label: "Technical" },
    { value: "non_technical",   label: "Non-technical" },
    { value: "sales",           label: "Sales-led" },
    { value: "marketing",       label: "Marketing-led" },
    { value: "operator",        label: "Operator / generalist" },
    { value: "mixed",           label: "Mixed" },
  ],
  team_skills: [
    { value: "development",    label: "Development" },
    { value: "design",         label: "Design" },
    { value: "marketing",      label: "Marketing" },
    { value: "sales",          label: "Sales" },
    { value: "operations",     label: "Operations" },
    { value: "finance",        label: "Finance" },
    { value: "product",        label: "Product" },
    { value: "ai_automation",  label: "AI & automation" },
    { value: "none",           label: "No formal skills yet" },
  ],
  hours_per_week: [
    { value: "lt_10",     label: "< 10 hrs / week" },
    { value: "10_20",     label: "10 – 20 hrs / week" },
    { value: "20_40",     label: "20 – 40 hrs / week" },
    { value: "40_plus",   label: "40+ hrs / week (full-time)" },
  ],
  operating_mode: [
    { value: "bootstrap", label: "Bootstrap",        tagline: "Capital efficient, organic growth" },
    { value: "funding",   label: "Raising funding",  tagline: "Velocity, scale, investor-ready" },
    { value: "unsure",    label: "Unsure yet",       tagline: "Defaults to bootstrap" },
  ],
  aggressiveness: [
    { value: "conservative",  label: "Conservative" },
    { value: "balanced",      label: "Balanced" },
    { value: "aggressive",    label: "Aggressive growth" },
    { value: "hyper_growth",  label: "Hyper-growth" },
  ],
  biggest_bottleneck: [
    { value: "clarity",      label: "Lack of clarity" },
    { value: "tech_skills",  label: "Missing tech skills" },
    { value: "users",        label: "Not enough users" },
    { value: "revenue",      label: "No revenue yet" },
    { value: "time",         label: "Not enough time" },
    { value: "funding",      label: "Lack of funding" },
    { value: "team",         label: "Weak team" },
    { value: "marketing",    label: "Weak marketing" },
    { value: "burnout",      label: "Burnout / motivation" },
    { value: "other",        label: "Other" },
  ],
  help_needed: [
    { value: "strategy",     label: "Strategy & decisions" },
    { value: "product",      label: "Product direction" },
    { value: "marketing",    label: "Marketing & growth" },
    { value: "sales",        label: "Sales & pipeline" },
    { value: "hiring",       label: "Hiring & team" },
    { value: "systems",      label: "Systems & ops" },
    { value: "fundraising",  label: "Fundraising" },
    { value: "legal",        label: "Legal & compliance" },
    { value: "motivation",   label: "Motivation / focus" },
  ],
};

export const SUCCESS_90D_MAX = 3;

// Fields that are REQUIRED (vs optional) for Continue to enable on each step.
export const REQUIRED_FIELDS_BY_STEP = {
  1: ["building", "target_customer", "core_problem", "revenue_model"],
  2: ["startup_stage", "main_goal", "target_timeline"], // success_90d optional
  3: ["target_market"], // country_based, country_of_incorporation optional
  4: ["budget_available", "funding_source"],
  5: ["team_size", "founder_role", "hours_per_week"], // team_skills optional
  6: ["operating_mode", "aggressiveness", "biggest_bottleneck", "help_needed"],
};

export const ALL_REQUIRED_FIELDS = Object.values(REQUIRED_FIELDS_BY_STEP).flat();

export function labelFor(field, value) {
  const table = OPTIONS[field];
  if (!table) return value;
  const hit = table.find((o) => o.value === value);
  return hit ? hit.label : value;
}

export function labelsFor(field, values) {
  return (values || []).map((v) => labelFor(field, v));
}
